/**
 * WelcomeBack — returning-visitor reactivation banner shown at the top of
 * /services when the visitor's localStorage already contains ≥1 completed
 * lite self-assessment snapshot.
 *
 * Reads from getAllSnapshots() (the same source the PortfolioRadar uses) so
 * the two stay in lockstep. Surfaces:
 *   • Completion progress (X of 12, % bar)
 *   • The next recommended sub-brand to take — picked as the one NOT yet
 *     completed whose service group complements the visitor's lowest-scoring
 *     sub-brand (cheap heuristic via a hard-coded affinity map)
 *   • A "Jump back in" CTA + a "Reset" link
 *
 * Dismissable per-session via sessionStorage so the visitor can ignore it
 * once and not see it again on the same visit.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronRight, X, RotateCcw, Flame } from "lucide-react";
import { getAllSnapshots } from "../lib/maturitySnapshots";
import { getDeviceId } from "../lib/deviceId";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const ALL_SLUGS = [
  "itsm360", "ai360", "data360", "pmo360", "change360", "digital360",
  "operating360", "supplier360", "benefits360", "govern360",
  "capability360", "stakeholder360",
];

// Sub-brand metadata for the CTA — code + headline only.
const META = {
  itsm360: { code: "ITSM360", q: 7, h: "Where is your IT service operation today?" },
  ai360: { code: "AI360", q: 6, h: "Where is your AI readiness today?" },
  data360: { code: "DATA360", q: 7, h: "Where is your data & analytics capability today?" },
  pmo360: { code: "PMO360", q: 5, h: "How disciplined is your PMO operation?" },
  change360: { code: "CHANGE360", q: 5, h: "How well does change stick in your organisation?" },
  digital360: { code: "DIGITAL360", q: 5, h: "How mature is your digital transformation?" },
  operating360: { code: "OPERATING360", q: 5, h: "Is your operating model delivering the strategy?" },
  supplier360: { code: "SUPPLIER360", q: 5, h: "Are you extracting value from your supplier estate?" },
  benefits360: { code: "BENEFITS360", q: 5, h: "How credible is your benefits-realisation discipline?" },
  govern360: { code: "GOVERN360", q: 5, h: "Is your governance a control function — or ceremony?" },
  capability360: { code: "CAPABILITY360", q: 5, h: "Do you know where your capability gaps are?" },
  stakeholder360: { code: "STAKEHOLDER360", q: 5, h: "How well does your organisation listen — and respond?" },
};

// "If they care about X, they probably also care about Y" — order matters,
// first match wins.
const AFFINITY = {
  itsm360: ["operating360", "supplier360", "data360"],
  ai360: ["data360", "govern360", "capability360"],
  data360: ["ai360", "benefits360", "govern360"],
  pmo360: ["benefits360", "govern360", "change360"],
  change360: ["capability360", "stakeholder360", "pmo360"],
  digital360: ["operating360", "data360", "change360"],
  operating360: ["itsm360", "digital360", "pmo360"],
  supplier360: ["itsm360", "operating360", "govern360"],
  benefits360: ["pmo360", "govern360", "data360"],
  govern360: ["benefits360", "pmo360", "ai360"],
  capability360: ["change360", "stakeholder360", "ai360"],
  stakeholder360: ["change360", "capability360", "govern360"],
};

const SESSION_KEY = "ekselens.welcomeDismissed";

export default function WelcomeBack() {
  const [snapshots, setSnapshots] = useState({});
  const [dismissed, setDismissed] = useState(false);
  const [inProgress, setInProgress] = useState(null); // {slug, sub_brand, answered_count, total_questions}

  useEffect(() => {
    setSnapshots(getAllSnapshots());
    try { setDismissed(sessionStorage.getItem(SESSION_KEY) === "1"); } catch { /* no-op */ }
  }, []);

  // Server-side recall — find any in-progress draft for this device. Cheap
  // single GET that returns null if nothing is in flight.
  useEffect(() => {
    const did = getDeviceId();
    if (!did) return;
    let cancelled = false;
    axios
      .get(`${API_BASE}/public/maturity/recall`, { params: { device_id: did } })
      .then(({ data }) => { if (!cancelled) setInProgress(data?.in_progress || null); })
      .catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, []);

  const { completed, total, pct, lowest, suggestion, streakDays } = useMemo(() => {
    const entries = Object.values(snapshots).filter((s) => s && s.overall > 0);
    const completedCount = entries.length;
    const totalCount = ALL_SLUGS.length;
    const pctVal = Math.round((completedCount / totalCount) * 100);
    // Lowest-scoring completed sub-brand (used as the affinity anchor)
    const sortedLow = [...entries].sort((a, b) => a.overall - b.overall);
    const lowestEntry = sortedLow[0] || null;
    // Find a "next" sub-brand: prefer affinity of the lowest-scoring completed
    // sub-brand; fall back to the first un-completed slug.
    let nextSlug = null;
    if (lowestEntry) {
      const wantList = AFFINITY[lowestEntry.slug] || [];
      nextSlug = wantList.find((s) => !snapshots[s]) || null;
    }
    if (!nextSlug) {
      nextSlug = ALL_SLUGS.find((s) => !snapshots[s]) || null;
    }
    // Completion-streak: count of consecutive UTC days the visitor has
    // completed at least one assessment, working backwards from today.
    // Reads `taken_at` (ISO) on each snapshot; falls back to 0 if not set.
    let streak = 0;
    const datesSeen = new Set();
    entries.forEach((e) => {
      const iso = e.updated_at || e.taken_at || e.completed_at;
      if (!iso) return;
      try { datesSeen.add(new Date(iso).toISOString().slice(0, 10)); } catch { /* noop */ }
    });
    if (datesSeen.size > 0) {
      const d = new Date();
      // allow today OR yesterday as the streak head (so a visitor returning
      // the next morning doesn't lose the streak before they engage today)
      const today = d.toISOString().slice(0, 10);
      const yesterday = new Date(d.getTime() - 86_400_000).toISOString().slice(0, 10);
      let cursor = datesSeen.has(today) ? d : (datesSeen.has(yesterday) ? new Date(d.getTime() - 86_400_000) : null);
      while (cursor && datesSeen.has(cursor.toISOString().slice(0, 10))) {
        streak += 1;
        cursor = new Date(cursor.getTime() - 86_400_000);
      }
    }
    return {
      completed: completedCount,
      total: totalCount,
      pct: pctVal,
      lowest: lowestEntry,
      suggestion: nextSlug ? { slug: nextSlug, ...META[nextSlug] } : null,
      streakDays: streak,
    };
  }, [snapshots]);

  if (dismissed) return null;
  if (completed === 0 && !inProgress) return null;
  if (!suggestion && !inProgress) return null; // visitor already completed all 12 and has no draft

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* no-op */ }
    setDismissed(true);
  };

  // Prefer the in-progress draft as the primary CTA — it's a warm continuation
  // ("you were 4 of 5 questions in") rather than a cold affinity guess.
  const useResume = !!inProgress;
  const targetSlug = useResume ? inProgress.slug : suggestion?.slug;
  const targetCode = (useResume ? META[inProgress.slug]?.code : suggestion?.code) || (inProgress?.sub_brand);
  const portfolioLabel = completed === 1
    ? "Your portfolio has one assessment so far"
    : completed === 0
    ? "You started a diagnostic but didn't finish"
    : `Your portfolio is ${pct}% complete`;

  return (
    <section
      className="bg-[#1A1124] text-white border-b border-[#7737F6]/30 relative overflow-hidden"
      data-testid="welcome-back-banner"
    >
      <div className="absolute inset-y-0 left-0 w-[6px] bg-[#7737F6]" />
      <div className="ek-container py-5 md:py-6">
        <div className="flex items-start md:items-center justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className="ek-eyebrow text-[#A179FA]">Welcome back</span>
              <span className="text-[11px] tracking-[0.18em] text-zinc-400 uppercase">{portfolioLabel}</span>
              {streakDays >= 2 && (
                <span
                  className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] bg-[#7737F6]/15 text-[#C0A8F8] border border-[#7737F6]/30 px-2 py-0.5"
                  data-testid="welcome-back-streak"
                  title={`You've engaged on ${streakDays} consecutive days`}
                >
                  <Flame size={11} /> {streakDays}-day streak
                </span>
              )}
            </div>
            {completed > 0 ? (
              <div className="flex items-center gap-3">
                <div className="h-1.5 bg-white/10 flex-1 max-w-[260px]" aria-label="Portfolio completion">
                  <div
                    className="h-full bg-[#7737F6] transition-all duration-500"
                    style={{ width: `${pct}%` }}
                    data-testid="welcome-back-progress"
                  />
                </div>
                <span className="text-[12px] text-zinc-300 tabular-nums">{completed} / {total}</span>
              </div>
            ) : null}
            {useResume ? (
              <p className="text-[12.5px] text-zinc-400 mt-2.5" data-testid="welcome-back-resume-msg">
                You were <strong className="text-white">{inProgress.answered_count} of {inProgress.total_questions} questions</strong> through the <strong className="text-white">{targetCode}</strong> diagnostic — pick up where you left off.
              </p>
            ) : lowest ? (
              <p className="text-[12.5px] text-zinc-400 mt-2.5">
                Your lowest-scoring sub-brand so far is <strong className="text-white">{META[lowest.slug]?.code || lowest.slug}</strong> at <strong className="text-[#A179FA]">{lowest.overall.toFixed(1)} / 4</strong>.
                We typically recommend <strong className="text-white">{suggestion.code}</strong> as the next conversation.
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            <Link
              to={`/resources/${targetSlug}-self-assessment`}
              className="inline-flex items-center gap-2 bg-[#7737F6] hover:bg-[#5E22D8] text-white px-4 py-2.5 text-[13px] font-semibold tracking-wide transition-colors"
              data-testid="welcome-back-continue"
            >
              {useResume ? <><RotateCcw size={13} /> Resume {targetCode}</> : <>Continue with {targetCode} <ChevronRight size={14} /></>}
            </Link>
            <button
              type="button"
              onClick={dismiss}
              className="text-zinc-400 hover:text-white p-1"
              aria-label="Dismiss"
              data-testid="welcome-back-dismiss"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
